// @ts-nocheck
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from 'react';
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useAction } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { CreditBalance } from '../shared/CreditBalance';
import { Id } from '@/convex/_generated/dataModel';
import { useToast } from '../ui/use-toast';
import { useConfetti } from '@/hooks/useConfetti';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { PageLoader, LOADER_MESSAGES } from '@/components/ui/loader';
import { motion, AnimatePresence } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import { formatDistanceToNow } from 'date-fns';
import { LinkedInIcon } from '@/components/ui/icons/linkedin-icon';
import { TwitterXIcon } from '@/components/ui/icons/twitter-x-icon';
import { useUserInfo } from '@/hooks/useUserInfo';
import {
	Wallet,
	ThumbsUp as ThumbsUpIcon,
	MessageSquare,
	Repeat2,
	BarChart2,
} from 'lucide-react';
import { SimpleTippingModal } from '@/components/social/SimpleTippingModal';
import { PostTipHistory } from '@/components/social/PostTipHistory';

const RANDOM_MESSAGE =
	LOADER_MESSAGES[Math.floor(Math.random() * LOADER_MESSAGES.length)];

const LOADER = <PageLoader message={RANDOM_MESSAGE} />;

interface FeedItem {
	metadata: any;
	_id: Id<'contentHistory'>;
	machineId: Id<'contentMachine'>;
	creatorUserId: Id<'users'>;
	creatorName: string;
	creatorEmail?: string;
	creatorImageUrl?: string;
	profileId?: Id<'profiles'>;
	profileKey?: string;
	profileWalletAddress?: string;
	platform: string;
	content: string;
	postUrl?: string;
	postId?: string;
	publishedAt: string;
	brandVoiceId?: string;
	likes?: number;
	comments?: number;
	shares?: number;
	views?: number;
	machineName: string;
	promptName?: string;
}

export function AdminDashboard() {
	const { user, isAdmin, isLoading: authLoading } = useAuth();
	const { getUserInfo } = useUserInfo();
	const [isLoading, setIsLoading] = useState(true);
	const { toast } = useToast();
	const { triggerConfetti, Confetti } = useConfetti();

	// States for the new Social Feed
	const [feedItems, setFeedItems] = useState<FeedItem[]>([]);
	const [hasMoreFeedItems, setHasMoreFeedItems] = useState(true);
	const [feedCursor, setFeedCursor] = useState<string | null>(null);
	const [isFetchingMore, setIsFetchingMore] = useState(false);
	const observer = useRef<IntersectionObserver | null>(null);

	// Forward declaration for fetchMoreFeedItems to satisfy useCallback dependency
	const fetchMoreFeedItemsRef = useRef<(() => Promise<void>) | null>(null);

	// Helper function to format post content with clickable links, hashtags, and mentions
	const formatPostContent = (content: string) => {
		// Split content into segments for processing
		const segments = content.split(/(\s+)/);

		return segments.map((segment, index) => {
			// Handle URLs
			if (segment.match(/^https?:\/\/[^\s]+/)) {
				return (
					<a
						key={index}
						href={segment}
						target='_blank'
						rel='noopener noreferrer'
						className='text-pink-400 hover:text-pink-300 underline'
					>
						{segment}
					</a>
				);
			}

			// Handle hashtags
			if (segment.match(/^#\w+/)) {
				return (
					<span key={index} className='text-pink-400 font-semibold'>
						{segment}
					</span>
				);
			}

			// Handle mentions
			if (segment.match(/^@\w+/)) {
				return (
					<span key={index} className='text-sky-400 font-semibold'>
						{segment}
					</span>
				);
			}

			// Regular text
			return segment;
		});
	};

	const loadMoreRef = useCallback(
		(node: HTMLElement | null) => {
			if (isLoading || isFetchingMore) return;
			if (observer.current) observer.current.disconnect();
			observer.current = new IntersectionObserver((entries) => {
				if (entries[0].isIntersecting && hasMoreFeedItems) {
					if (fetchMoreFeedItemsRef.current) {
						fetchMoreFeedItemsRef.current();
					}
				}
			});
			if (node) observer.current.observe(node);
		},
		[isLoading, isFetchingMore, hasMoreFeedItems]
	);

	// State for Tipping Modal
	const [tippingModalOpen, setTippingModalOpen] = useState(false);
	const [tipRecipient, setTipRecipient] = useState<{
		name: string;
		address: string;
		contentHistoryId?: Id<'contentHistory'>;
	} | null>(null);

	// Keep Version update dialog states and functions
	const [showVersionDialog, setShowVersionDialog] = React.useState(false);
	const [newVersion, setNewVersion] = React.useState('');
	const [versionNotes, setVersionNotes] = React.useState('');
	const setVersionMutation = useMutation(api.systemInfo.setVersion);
	const currentVersion = useQuery(api.systemInfo.getVersion);

	// Helper to transform ContentHistory (and related machine data) to FeedItem
	const transformContentHistoryToFeedItems = useCallback(
		(historyItems: any[]): FeedItem[] => {
			return historyItems
				.map((item, index) => {
					const machine = item.machine;
					const userInfo = machine
						? getUserInfo(machine.userId as Id<'users'>)
						: {
								name: 'Unknown',
								email: '',
								walletAddress: undefined,
							};
					const creatorImageUrl =
						(userInfo as any)?.imageUrl || undefined;

					// Use the user's wallet address from the database, not the profile wallet
					const userWalletAddress = userInfo.walletAddress || '';

					const transformedItem = {
						_id: item._id as Id<'contentHistory'>,
						machineId: item.machineId as Id<'contentMachine'>,
						creatorUserId: machine?.userId as Id<'users'>,
						creatorName: userInfo.name || 'Anonymous',
						creatorEmail: userInfo.email,
						creatorImageUrl: creatorImageUrl,
						profileId: machine?.profileId,
						profileKey: machine?.profileKey,
						profileWalletAddress: userWalletAddress,
						platform: machine?.platform || 'unknown',
						content: item.content || '',
						postUrl: item.metadata?.postUrl,
						postId: item.metadata?.postId,
						publishedAt:
							item.metadata?.publishedAt || item.createdAt,
						brandVoiceId: item.brandVoice?.id,
						likes: item.metadata?.engagement?.likes || 0,
						comments: item.metadata?.engagement?.comments || 0,
						shares: item.metadata?.engagement?.shares || 0,
						views:
							item.metadata?.engagement?.views ||
							item.metadata?.engagement?.impressions ||
							0,
						machineName: machine?.name || 'Content Machine',
						promptName: machine?.promptName,
						metadata: item.metadata,
					};

					return transformedItem;
				})
				.sort(
					(a, b) =>
						new Date(b.publishedAt).getTime() -
						new Date(a.publishedAt).getTime()
				);
		},
		[getUserInfo]
	);

	// Fetch initial feed items
	const initialFeedData = useQuery(api.api.getPaginatedContentHistory, {
		cursor: '',
		limit: 10,
	});

	useEffect(() => {
		if (initialFeedData && Array.isArray(initialFeedData.items)) {
			const transformedItems = transformContentHistoryToFeedItems(
				initialFeedData.items
			);

			setFeedItems(transformedItems);
			setFeedCursor(initialFeedData.nextCursor);
			setHasMoreFeedItems(initialFeedData.hasMore);
			setIsLoading(false);
		} else if (initialFeedData === undefined && !authLoading && user) {
			setIsLoading(true);
		} else if (
			initialFeedData === null ||
			(initialFeedData && !Array.isArray(initialFeedData.items))
		) {
			setIsLoading(false);
			setHasMoreFeedItems(false);
			setFeedItems([]); // Ensure feed items are cleared
		}
	}, [
		initialFeedData,
		authLoading,
		user,
		transformContentHistoryToFeedItems,
	]);

	const fetchMoreContentHistoryMutation = useMutation(
		api.api.getPaginatedContentHistorymutation
	);

	fetchMoreFeedItemsRef.current = async () => {
		if (!hasMoreFeedItems || isFetchingMore || !feedCursor) return;
		setIsFetchingMore(true);
		try {
			const moreData = await fetchMoreContentHistoryMutation({
				cursor: feedCursor,
				limit: 10,
			});
			if (moreData && Array.isArray(moreData.items)) {
				const newTransformedItems = transformContentHistoryToFeedItems(
					moreData.items
				);
				setFeedItems((prevItems) => [
					...prevItems,
					...newTransformedItems,
				]);
				setFeedCursor(moreData.nextCursor);
				setHasMoreFeedItems(moreData.hasMore);
			} else {
				setHasMoreFeedItems(false); // Stop fetching if data is malformed or no items
			}
		} catch (error) {
			console.error('Error fetching more feed items:', error);
			toast({
				title: 'Error',
				description: 'Could not load more posts.',
				variant: 'destructive',
			});
		}
		setIsFetchingMore(false);
	};

	// Query tip analytics for social feed enhancement
	const allUserTipStats = useQuery(api.tipAnalytics.getAllUserTipStats, {});
	const allPostTipStats = useQuery(api.tipAnalytics.getAllPostTipStats, {});

	// Helper functions for tip analytics
	const getUserTipStats = useCallback(
		(userId: Id<'users'>) => {
			return allUserTipStats?.find(
				(stats: any) => stats.userId === userId
			);
		},
		[allUserTipStats]
	);

	const getPostTipStats = useCallback(
		(contentHistoryId: Id<'contentHistory'>) => {
			return allPostTipStats?.find(
				(stats: any) => stats.contentHistoryId === contentHistoryId
			);
		},
		[allPostTipStats]
	);

	const formatKarmaAmount = useCallback((amount: string) => {
		const num = parseFloat(amount);
		if (num >= 1000) {
			return `${(num / 1000).toFixed(1)}K`;
		}
		return num.toFixed(2);
	}, []);

	const handleTip = (
		recipientName: string,
		recipientWalletAddress: string,
		contentHistoryId?: Id<'contentHistory'>
	) => {
		if (
			!recipientWalletAddress ||
			!recipientWalletAddress.startsWith('0x')
		) {
			toast({
				title: 'Cannot Tip',
				description:
					'This profile does not have a valid wallet address set up for tipping.',
				variant: 'destructive',
			});
			return;
		}
		setTipRecipient({
			name: recipientName,
			address: recipientWalletAddress,
			contentHistoryId, // Store contentHistoryId for analytics
		});
		setTippingModalOpen(true);
	};

	const handleTipSuccess = (queueId: string, tippedAmount: string) => {
		triggerConfetti();
		toast({
			title: '✨ Tip Sent!',
			description: `Successfully tipped ${tipRecipient?.name} ${tippedAmount} KARMA. Queue ID: ${queueId.substring(0, 10)}...`,
			duration: 5000,
		});
	};

	useEffect(() => {
		if (authLoading) {
			setIsLoading(true);
			return;
		}
		if (!user) {
			setIsLoading(false);
			return;
		}
	}, [authLoading, user]);

	if (isLoading && feedItems.length === 0) {
		return LOADER;
	}

	return (
		<div className='container mx-auto p-6 pb-20 relative'>
			<Confetti />
			<div className='flex justify-between items-center mb-6'>
				<h1 className='text-3xl font-bold py-8'>Dashboard</h1>
				<div className='flex items-center gap-4'>
					{(user?.email === 'reverend@thewatchersociety.org' ||
						user?.email === 'matt@thetrainermethod.com') && (
						<Button
							variant='outline'
							onClick={() => {
								setNewVersion(currentVersion?.value ?? '0.0.0');
								setVersionNotes(currentVersion?.notes ?? '');
								setShowVersionDialog(true);
							}}
							className='mr-4'
						>
							Update Version
						</Button>
					)}
					{user && (
						<div className='flex items-center gap-2'>
							<CreditBalance
								userId={user.id as unknown as Id<'users'>}
							/>
						</div>
					)}
				</div>
			</div>

			<Dialog
				open={showVersionDialog}
				onOpenChange={(open) => {
					if (!open) {
						setNewVersion(''); // Reset on close
						setVersionNotes('');
					}
					setShowVersionDialog(open);
				}}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Update System Version</DialogTitle>
					</DialogHeader>
					<div className='space-y-4'>
						<div>
							<label
								htmlFor='versionNumber'
								className='text-sm font-medium mb-2 block'
							>
								Version Number
							</label>
							<Input
								id='versionNumber'
								placeholder={`Current version: ${currentVersion?.value ?? '0.0.0'}`}
								value={newVersion}
								onChange={(e) => setNewVersion(e.target.value)}
							/>
						</div>
						<div>
							<label
								htmlFor='versionNotes'
								className='text-sm font-medium mb-2 block'
							>
								Release Notes
							</label>
							<textarea
								id='versionNotes'
								className='w-full min-h-[100px] px-3 py-2 rounded-md border bg-background text-sm dark:bg-zinc-800 dark:border-zinc-700 dark:text-white'
								placeholder='Enter release notes (optional)'
								value={versionNotes}
								onChange={(e) =>
									setVersionNotes(e.target.value)
								}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button
							variant='outline'
							onClick={() => setShowVersionDialog(false)}
						>
							Cancel
						</Button>
						<Button
							onClick={async () => {
								if (newVersion.trim() && user?.id) {
									try {
										const result = await setVersionMutation(
											{
												value: newVersion.trim(),
												notes:
													versionNotes.trim() ||
													undefined,
												// Assuming setVersionMutation might need requestingUserId or similar for auth
												// requestingUserId: user.id as Id<'users' >
											}
										);
										// Check result if your mutation returns status
										toast({
											title: '🚀 Version Updated 🎉',
											description: `✨ System version ${newVersion.trim()} is now live! 🌟`,
											duration: 3000,
										});
										setShowVersionDialog(false);
									} catch (err) {
										toast({
											title: 'Error updating version',
											description: (err as Error).message,
											variant: 'destructive',
										});
									}
								}
							}}
							disabled={
								!newVersion.trim() ||
								(newVersion.trim() === currentVersion?.value &&
									versionNotes.trim() ===
										(currentVersion?.notes ?? ''))
							}
						>
							Update Version
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* ==== NEW SOCIAL FEED SECTION ==== */}
			<Card className='border-primary/20 dark:border-pink-900/50 dark:bg-gradient-to-b dark:from-zinc-900 dark:to-black bg-white/80 shadow-md overflow-hidden mb-6'>
				<CardHeader>
					<CardTitle className='text-2xl font-bold text-white pb-2'>
						Social Feed
					</CardTitle>
				</CardHeader>
				<CardContent className='p-4'>
					{feedItems.length === 0 &&
						!isLoading &&
						!isFetchingMore && (
							<div className='flex flex-col items-center justify-center p-6 rounded-lg bg-gradient-to-br from-zinc-800/40 to-zinc-900/40 border border-pink-900/20 text-sm min-h-[400px]'>
								<p className='text-gray-400 text-center'>
									No posts in the feed yet. Check back later
									or ensure content machines are active!
								</p>
							</div>
						)}
					<div className='space-y-6'>
						{feedItems.map((item) => {
							return (
								<motion.div
									key={item._id}
									className='flex flex-col p-4 rounded-lg bg-gradient-to-br from-zinc-800/40 to-zinc-900/40 border border-pink-900/20 hover:border-pink-600/40 hover:from-zinc-800/60 hover:shadow-[0_0_10px_rgba(236,72,153,0.15)] transition-all duration-200 group'
									initial={{ opacity: 0, y: 20 }}
									animate={{ opacity: 1, y: 0 }}
									exit={{ opacity: 0, y: -20 }}
									transition={{ duration: 0.3 }}
								>
									<div className='flex items-start space-x-4 mt-6'>
										<div className='flex-shrink-0'>
											{item.creatorImageUrl ? (
												<img
													src={item.creatorImageUrl}
													alt={item.creatorName}
													className='h-12 w-12 rounded-full object-cover border-2 border-pink-500/50'
												/>
											) : (
												<div className='h-12 w-12 rounded-full bg-gradient-to-tr from-purple-900 to-pink-500 flex items-center justify-center text-white text-lg font-bold border-2 border-pink-500/50'>
													{item.creatorName
														?.charAt(0)
														.toUpperCase() || '?'}
												</div>
											)}
										</div>

										<div className='flex-grow min-w-0'>
											<div className='flex items-center justify-between mb-2'>
												<div className='flex items-center space-x-2'>
													<span className='font-semibold text-white text-lg truncate'>
														{item.creatorName}
													</span>

													<Badge
														className={`text-sm font-semibold px-2 py-1 border-transparent ${
															item.platform ===
															'twitterx'
																? 'bg-gradient-to-r from-slate-600 to-zinc-700 text-white'
																: item.platform ===
																	  'linkedin'
																	? 'bg-gradient-to-r from-blue-600 to-indigo-700 text-white'
																	: 'bg-gradient-to-r from-purple-600 to-pink-700 text-white'
														}`}
													>
														{item.platform ===
														'twitterx'
															? '𝕏'
															: item.platform ===
																  'linkedin'
																? 'LinkedIn'
																: item.platform}
													</Badge>
												</div>

												<span className='text-gray-400 text-lg mr-8'>
													{formatDistanceToNow(
														new Date(
															item.publishedAt
														),
														{ addSuffix: true }
													)}
												</span>
											</div>
											<div>
												{/* User Earnings Display */}
												{(() => {
													const userStats =
														getUserTipStats(
															item.creatorUserId
														);
													if (
														userStats &&
														parseFloat(
															userStats.totalAmountReceived
														) > 0
													) {
														return (
															<>
																<span className='text-lg text-gray-400 mr-2'>
																	Earnings:
																</span>
																<span className='inline-flex items-center gap-1 px-3 py-1 text-lg font-semibold bg-gradient-to-r from-emerald-500/20 to-green-500/20 border border-emerald-500/30 text-emerald-400 rounded-full'>
																	<Wallet className='h-5 w-5' />
																	{formatKarmaAmount(
																		userStats.totalAmountReceived
																	)}{' '}
																	KARMA
																</span>
															</>
														);
													}
													return null;
												})()}
											</div>

											<div className='mt-3 p-4 rounded-lg bg-black/30 border border-white/5'>
												<div className='text-white/95 text-base whitespace-pre-wrap break-words leading-relaxed'>
													{formatPostContent(
														item.content
													)}
												</div>
												{item.postUrl && (
													<div className='mt-3'>
														<a
															href={item.postUrl}
															target='_blank'
															rel='noopener noreferrer'
															className='inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-pink-500/20 to-purple-600/20 hover:from-pink-500/30 hover:to-purple-600/30 border border-pink-500/30 text-pink-300 hover:text-pink-200 transition-all duration-200 group text-sm'
														>
															<span className='font-medium'>
																View full post
																on{' '}
																{item.platform ===
																'twitterx'
																	? '𝕏'
																	: item.platform ===
																		  'linkedin'
																		? 'LinkedIn'
																		: item.platform}
															</span>
															<span className='ml-1 opacity-70 group-hover:opacity-100 transition-opacity'>
																→
															</span>
														</a>
													</div>
												)}
											</div>
											{/* Post Earnings Display */}
											{(() => {
												const postStats =
													getPostTipStats(item._id);
												if (
													postStats &&
													parseFloat(
														postStats.totalAmount
													) > 0
												) {
													return (
														<div className='mt-3 mb-2 flex items-center gap-3'>
															<span className='text-lg text-gray-400'>
																Post Earnings:
															</span>
															<span className='inline-flex items-center gap-1 px-3 py-1 text-lg font-semibold bg-gradient-to-r from-pink-500 to-purple-500 border border-pink-500 text-white rounded-full'>
																<Wallet className='h-5 w-5' />
																{formatKarmaAmount(
																	postStats.totalAmount
																)}{' '}
																KARMA
																<span className='text-pink-200'>
																	•{' '}
																	{
																		postStats.totalTips
																	}{' '}
																	rewards
																</span>
															</span>
														</div>
													);
												}
												return null;
											})()}

											{/* Actions Row */}
											<div className='mt-4 mb-4 pt-4 border-t border-pink-500/20 flex items-center justify-between gap-4'>
												<Button
													variant='ghost'
													size='sm'
													className='px-5 py-4 text-lg font-semibold rounded-full transition-all duration-200 flex items-center space-x-2 shadow-md hover:shadow-lg transform hover:scale-105 bg-gradient-to-r from-emerald-500 to-green-600 text-white border-transparent hover:from-emerald-600 hover:to-green-700'
													onClick={() =>
														handleTip(
															item.creatorName,
															item.profileWalletAddress ||
																'',
															item._id // Pass contentHistoryId for post-specific analytics
														)
													}
													disabled={
														!item.profileWalletAddress?.startsWith(
															'0x'
														)
													}
													title={
														!item.profileWalletAddress?.startsWith(
															'0x'
														)
															? 'Tipping not available for this profile'
															: 'Tip this post'
													}
												>
													<ThumbsUpIcon className='h-4 w-4' />
													<span>SEND TIP</span>
												</Button>
											</div>

											{/* Recent Tips Section */}
											<PostTipHistory
												contentHistoryId={item._id}
											/>
										</div>
									</div>
								</motion.div>
							);
						})}
						{isFetchingMore && (
							<div className='flex flex-col items-center justify-center p-6 rounded-lg bg-gradient-to-br from-zinc-800/40 to-zinc-900/40 border border-pink-900/20'>
								<PageLoader message='LOADING MORE POSTS...' />
							</div>
						)}
						{!isFetchingMore &&
							hasMoreFeedItems &&
							feedItems.length > 0 && (
								<div
									ref={loadMoreRef}
									className='h-10 flex justify-center items-center'
								>
									{/* Optionally show a subtle loader or just let it auto-load */}
									{/* <span className='text-zinc-500'>Loading more...</span> */}
								</div>
							)}
						{!hasMoreFeedItems && feedItems.length > 0 && (
							<div className='flex flex-col items-center justify-center p-6 rounded-lg bg-gradient-to-br from-zinc-800/40 to-zinc-900/40 border border-pink-900/20'>
								<p className='text-gray-400 text-center'>
									You've reached the end of the feed! 🎉
								</p>
							</div>
						)}
					</div>
				</CardContent>
			</Card>
			{/* ==== END NEW SOCIAL FEED SECTION ==== */}

			{tipRecipient && (
				<SimpleTippingModal
					isOpen={tippingModalOpen}
					onClose={() => {
						setTippingModalOpen(false);
						setTipRecipient(null); // Clear recipient when closing
					}}
					onSuccess={handleTipSuccess}
					recipientName={tipRecipient.name}
					recipientAddress={tipRecipient.address}
					contentHistoryId={tipRecipient.contentHistoryId}
				/>
			)}
		</div>
	);
}
